import {
	Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	ChangeDetectorRef,
	EventEmitter,
	Output,
	ViewChild
} from '@angular/core';
import { DateService, MessageService } from 'base';
import { NavigationService, NavLocation, NavType } from 'netview';
//import { Table } from 'primeng/table';
import { UvTable } from 'widget';
import { SearchDeviceIdentityDto, SearchDeviceListDto, SearchDeviceListEntryDto, SearchDeviceObservationDto } from 'UvExplorerServer.Dtos.TS/Discovery';


export interface DeviceSearchTableEntry {
	guid: string;
	deviceName: string;
	ipAddress: string;
	macAddress: string;
	serialNumber: string;
	timeStamp: string;
	owner: string;
	network: string;
	agent: string[];
	discovery: string[];
	tag: SearchDeviceListEntryDto;
}


@Component({
    selector: 'device-search',
	templateUrl: './device-search.component.html',
    styleUrls: ['./device-search.component.scss']
})
export class DeviceSearchComponent implements OnInit, OnDestroy {

	@Input()
	public visible: boolean;

	@Input()
	public deviceList: SearchDeviceListEntryDto[];

	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Output()
	public deviceSelected: EventEmitter<DeviceSearchTableEntry>;

	@ViewChild('tt', { static: true })
	public table: UvTable;

	public cols: any[];
	public deviceEntries: DeviceSearchTableEntry[];
	public selectedEntry: DeviceSearchTableEntry;
	public searchText: string;
	
	public readonly VirtualScrollItemSize = 29;
	public get TableRowStyle(): string {
		return `height: ${this.VirtualScrollItemSize}px;`;
	}

	constructor(
		private messageService: MessageService,
		private dateService: DateService,
		private navService: NavigationService,
		private changeDetector: ChangeDetectorRef) {

		this.visible = false;
		this.deviceList = null;
		this.visibleChange = new EventEmitter<boolean>();
		this.deviceSelected = new EventEmitter<DeviceSearchTableEntry>();

		this.cols = [
			{ field: 'deviceName', header: 'Device Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'ipAddress', header: 'IP Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'macAddress', header: 'MAC Address', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'serialNumber', header: 'Serial Number', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'timeStamp', header: 'Timestamp', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'owner', header: 'Owner', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'network', header: 'Network', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'agent', header: 'Agent', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'discovery', header: 'Discovery', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];

		this.deviceEntries = [];
		this.selectedEntry = null;
		this.searchText = "";
    }

	public doCustomSort(event): void {
	}

	public ngOnInit() {
		return;
	}

	public ngOnDestroy(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes["deviceList"]) {
			this.rebuildEntries();
		}
		if (changes["visible"]) {
			this.searchText = "";
			this.selectedEntry = null;
			this.table.filterGlobal(this.searchText, 'contains');
		}
	}

	private rebuildEntries(): void {
		this.deviceEntries = [];
		
		if (!!this.deviceList && (this.deviceList.length > 0)) {
			this.deviceList.forEach((sdlEntry: SearchDeviceListEntryDto) => {
				this.deviceEntries.push({
					guid: sdlEntry.guid,
					deviceName: this.getDeviceName(sdlEntry),
					ipAddress: this.safeArrayJoin(sdlEntry.identity.ipAddresses),
					macAddress: this.safeArrayJoin(sdlEntry.identity.macAddresses),
					serialNumber: this.safeArrayJoin(sdlEntry.identity.serialNumbers),
					timeStamp: this.getTimeStamp(sdlEntry),
					owner: sdlEntry.userName,
					network: sdlEntry.scopeName,
					agent: sdlEntry.deviceObservations.map(o => o.agentName),
					discovery: sdlEntry.deviceObservations.map(o => o.schedDiscName),
					tag: sdlEntry,
				});
			});
		}
	}

	private getDeviceName(sdlEntry: SearchDeviceListEntryDto): string {
		let names: string[] = [];

		let identity: SearchDeviceIdentityDto = sdlEntry.identity;
		if (!!identity.hostName) {
			names.push(identity.hostName);
		}
		if (!!identity.systemName) {
			names.push(identity.systemName);
		}
		if (!!identity.netBiosName) {
			if (!!identity.netBiosDomain) {
				names.push(`${identity.netBiosDomain}\\${identity.netBiosName}`);
			}
			else {
				names.push(identity.netBiosName);
			}
		}
		if (!!identity.dependentDeviceControllerLabel) {
			names.push(identity.dependentDeviceControllerLabel);
		}

		return this.safeArrayJoin(names);
	}

	private getTimeStamp(sdlEntry: SearchDeviceListEntryDto): string {
		return this.safeArrayJoin(
				sdlEntry.deviceObservations.map(
					(ob) => this.dateService.formatDateHm(new Date(ob.timeStampUtc))));
	}

	private safeArrayJoin(array: string[]): string {
		return (!!array ? array.join(", ") : "");
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);

		this.changeDetector.markForCheck();
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);

		this.changeDetector.markForCheck();
	}

	public get enableOkButton(): boolean {
		return !!this.selectedEntry;
	}

	public onDeviceSelected(entry: DeviceSearchTableEntry): void {
		this.hide();
		this.deviceSelected.emit(entry);	}

	public onDialogHide(): void {
		this.hide();
	}

	public onOkClicked(): void {
		this.hide();
		this.deviceSelected.emit(this.selectedEntry);
	}

	public onCancelClicked(): void {
		this.hide();
	}

	public gotoNetwork(scopeGuid: string): void {
		let newNavLoc = new NavLocation(NavType.Network);
		newNavLoc.netViewGuid = scopeGuid;
		this.navService.selectLocation(newNavLoc);
		//this.navService.selectUrl(`/network/${scopeGuid}`);
		this.hide();
	}

	public gotoAgent(scopeGuid:string, agentGuid: string): void {
		let newNavLoc = new NavLocation(NavType.Agent);
		newNavLoc.netViewGuid = scopeGuid;
		newNavLoc.agentGuid = agentGuid;
		this.navService.selectLocation(newNavLoc);
		//this.navService.selectUrl(`/agent/${agentGuid}`);
		this.hide();
	}

	public gotoDiscovery(scopeGuid: string, agentGuid: string, discGuid: string): void {
		let newNavLoc = new NavLocation(NavType.Discovery);
		newNavLoc.netViewGuid = scopeGuid;
		newNavLoc.agentGuid = agentGuid;
		newNavLoc.schedDiscGuid = discGuid;
		this.navService.selectLocation(newNavLoc);
		//this.navService.selectUrl(`/discovery/${discGuid}`);
		this.hide();		
	}

}
